import {
  Account,
  AccountBalance,
  AccountBalanceChart,
  AccountDailyPerformance,
  AccountStats,
  ConsistencyRuleResult,
  ConsistencyRules,
  ConsistencyRulesValues,
  MidnightValue,
  TradingSystem,
  UserTradingSystemCredentials,
} from '@monorepo/types';
import { BaseResponse } from '../core/base-entity-service';
import CrudService from '../core/crud-service';

export default class AccountService extends CrudService {
  static override route = 'account';

  async getLogins() {
    const response = await this.httpService.get<
      BaseResponse<{ logins: UserTradingSystemCredentials[] }>
    >(`${this.path}/logins`);

    if (!response.data.logins) {
      throw new Error(`Missing logins`);
    }

    return response.data.logins;
  }

  async decryptPassword(login: string, tradingSystem: TradingSystem) {
    try {
      const response = await this.httpService.post<
        { login: string; tradingSystem: TradingSystem },
        { data: string }
      >(`${this.path}/decrypt-password`, { login, tradingSystem });

      return response.data;
    } catch (e) {
      console.error(`Failed decrypting password for login: ${login}`, e);
      throw new Error('Failed decrypting password');
    }
  }

  async getConsistencyRules(tsAccountId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<Record<ConsistencyRules, ConsistencyRuleResult>>
      >(`${this.path}/${tsAccountId}/consistency-rules`);

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting account consistency rules: ${tsAccountId}`,
        e,
      );
      throw e;
    }
  }

  async getConsistencyRule(tsAccountId: string, rule: ConsistencyRules) {
    try {
      const response = await this.httpService.get<
        BaseResponse<ConsistencyRulesValues>
      >(`${this.path}/${tsAccountId}/consistency-rule/${rule}`);

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting account consistency rule: ${tsAccountId}, rule: ${rule}`,
        e,
      );
      throw e;
    }
  }

  async getStats(tsAccountId: string) {
    try {
      const response = await this.httpService.get<BaseResponse<AccountStats>>(
        `${this.path}/${tsAccountId}/stats`,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed getting account stats: ${tsAccountId}`, e);
      throw e;
    }
  }

  async getDailyPerformance(tsAccountId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<AccountDailyPerformance>
      >(`${this.path}/${tsAccountId}/daily-performance`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting daily performance: ${tsAccountId}`, e);
      throw e;
    }
  }

  async getBalanceChart(tsAccountId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<AccountBalanceChart[]>
      >(`${this.path}/${tsAccountId}/balance-chart`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting account balance chart: ${tsAccountId}`, e);
      throw e;
    }
  }

  async getBalance(tsAccountId: string) {
    try {
      const response = await this.httpService.get<BaseResponse<AccountBalance>>(
        `${this.path}/${tsAccountId}/balance`,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed getting account balance: ${tsAccountId}`, e);
      throw e;
    }
  }

  async getMidnightHistory(tsAccountId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<MidnightValue[]>
      >(`${this.path}/${tsAccountId}/midnight-history`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting  midnight history: ${tsAccountId}`, e);
      throw e;
    }
  }

  async sync(tsAccountId: string, force = false) {
    try {
      await this.httpService.post(`${this.path}/${tsAccountId}/sync`, {
        force,
      });

      return true;
    } catch (e) {
      console.error(`Failed syncing account: ${tsAccountId}`, e);
      throw e;
    }
  }

  async getByPurchaseUniqId(purchaseUniqId: string) {
    try {
      const response = await this.httpService.get<BaseResponse<Account>>(
        `${this.path}/purchase/${purchaseUniqId}`,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed getting account: ${purchaseUniqId}`, e);
      throw e;
    }
  }

  async getByTradingSystemAccount(tsAccountId: string) {
    try {
      const response = await this.httpService.get<BaseResponse<Account>>(
        `${this.path}/ts/${tsAccountId}`,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed getting account: ${tsAccountId}`, e);
      throw e;
    }
  }

  async selectPartner(orderUniqId: string, partnerId: string) {
    try {
      const response = await this.httpService.post<
        { orderUniqId: string; partnerId: string },
        BaseResponse<Account>
      >(`${this.path}/partner-select`, {
        orderUniqId,
        partnerId,
      });

      return response.data;
    } catch (e) {
      console.error(
        `Failed selecting partner: ${partnerId}, for order: ${orderUniqId}`,
        e,
      );
      throw e;
    }
  }

  async markBannerAsSeen(accountId: string) {
    try {
      await this.httpService.post(
        `${this.path}/${accountId}/mark-banner-as-seen`,
      );

      return true;
    } catch (e) {
      console.error(`Failed marking banner as seen: ${accountId}`, e);
      throw e;
    }
  }

  async markTerminationReasonAsSeen(tsAccountId: string) {
    try {
      await this.httpService.post(
        `${this.path}/${tsAccountId}/seen-termination-reason`,
      );

      return true;
    } catch (e) {
      console.error(
        `Failed marking termination reason as seen: ${tsAccountId}`,
        e,
      );
      throw e;
    }
  }

  async processAddOn({
    accountId,
    addOnId,
  }: {
    accountId: string;
    addOnId: string;
  }) {
    try {
      await this.httpService.put(`${this.path}/${accountId}/process-addon`, {
        addOnId,
      });

      return true;
    } catch (e) {
      console.error(`Failed adding add-on`, e);

      throw e;
    }
  }
}
